/** Ownership & Disclosures tab.
 *
 * Reuses the existing cockpit visual language directly: holder position bars,
 * insider scatter, grouped-politician buy/sell split and the Form 144
 * cumulative proposed-share visual. Congressional dollars are never presented
 * as exact — aggregates say "bracket-midpoint estimate" and the transaction
 * table shows the disclosed range.
 */
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { InstitutionalFlowChart } from "@/cockpit/shadow/InstitutionalFlowChart";
import { InsiderScatterPlot } from "@/cockpit/shadow/InsiderScatterPlot";
import { PoliticalFlowTimeline } from "@/cockpit/shadow/PoliticalFlowTimeline";
import { DilutionPressureLine } from "@/cockpit/shadow/DilutionPressureLine";
import { compactUsd } from "@/cockpit/formatCockpit";
import { CachedBadge, DataNotice, EmptyNote, MockBadge, PanelGrid, WorkspacePanel } from "@/workspace/primitives";
import { ownership } from "@/workspace/mockTickerData";

export function OwnershipTab({ t }) {
  if (t.live && (t.resourceStatus?.ownership === "loading" || t.resourceStatus?.ownership === "idle")) {
    return (
      <WorkspacePanel title="Ownership & Disclosures" testid="ownership-loading">
        <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Loading cached ownership data…</p>
      </WorkspacePanel>
    );
  }
  if (t.live && t.resourceStatus?.ownership === "error") {
    return (
      <WorkspacePanel title="Ownership & Disclosures" testid="ownership-error">
        <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
          {t.resourceStatus.ownershipError || "data unavailable"}
        </p>
      </WorkspacePanel>
    );
  }
  const o = t.live ? (t.ownership || { holders: [], insider: [], congress: [], proposedSales: [], buyCount30d: 0, sellCount30d: 0 }) : ownership(t.ticker);
  const aggregate = o.congress.reduce((s, r) => s + (r.amountMidpointUsd || 0), 0);

  return (
    <div className="space-y-3">
      <PanelGrid>
        <InstitutionalFlowChart holders={o.holders} flows={[]} note={t.live ? (o.institutionsNote || "13F is a lagged holdings snapshot, not a buy or sell tape.") : "Mock snapshot for layout review."} />
        <InsiderScatterPlot
          activity={o.insider}
          buyCount30d={o.buyCount30d}
          sellCount30d={o.sellCount30d}
        />
      </PanelGrid>

      <PanelGrid>
        <PoliticalFlowTimeline timeline={[]} trades={o.congress} />
        <DilutionPressureLine sales={o.proposedSales} dataSourceStatus={t.live ? "cached" : "mock"} />
      </PanelGrid>

      <WorkspacePanel
        title="Congressional transactions · disclosed ranges"
        subtitle="Transaction level shows the disclosed bracket. Only aggregates use a midpoint estimate."
        notice={t.live ? <CachedBadge /> : <MockBadge />}
        hint={t.live ? "Congressional amounts are disclosed as brackets, not exact dollars." : "Fixture rows shaped like the existing house-feed congress entries (date, person, chamber, type, amount)."}
        actions={<StatusBadge tier="info" text="Bracket-midpoint estimate" />}
        testid="ownership-ptr-transactions"
      >
        {o.congress.length ? (
          <div className="overflow-x-auto min-w-0">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left uppercase tracking-wide text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  <th className="py-1 pr-3 font-medium">Trade date</th>
                  <th className="py-1 pr-3 font-medium">Filer</th>
                  <th className="py-1 pr-3 font-medium">Chamber</th>
                  <th className="py-1 pr-3 font-medium">Direction</th>
                  <th className="py-1 pr-3 font-medium">Disclosed range</th>
                  <th className="py-1 font-medium">Disclosure delay</th>
                </tr>
              </thead>
              <tbody>
                {o.congress.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="py-1.5 pr-3 font-mono-data text-[10px] whitespace-nowrap">{r.tradeDate}</td>
                    <td className="py-1.5 pr-3 truncate">{r.politicianName}</td>
                    <td className="py-1.5 pr-3">{r.chamber}</td>
                    <td className="py-1.5 pr-3" style={{ color: r.type === "Sell" ? "#EF4444" : "#10B981" }}>
                      {r.type === "Sell" ? "▼ Sell" : "▲ Buy"}
                    </td>
                    <td className="py-1.5 pr-3 font-mono-data text-[10px] whitespace-nowrap">{r.amountBracket}</td>
                    <td className="py-1.5">
                      {r.delayFlag
                        ? <StatusBadge tier="caution" text={`${r.delayDays}d after trade`} />
                        : <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Within 30 days</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
              Aggregate across these filings is a bracket-midpoint estimate of {compactUsd(aggregate)}. Exact dollar
              amounts are not disclosed under the STOCK Act.
            </p>
          </div>
        ) : (
          <EmptyNote>No congressional filings for this ticker in the window.</EmptyNote>
        )}
      </WorkspacePanel>

      <WorkspacePanel title="How to read these disclosures" testid="ownership-footnote">
        <DataNotice
          adminText="Panels above reuse the production cockpit components. In this mockup they render fixtures; in production they read the cached house-feed shadow document (insider, institutions.top_holders, congress, proposed_sales)."
          plainText="Data may be delayed. Filings can be amended or incomplete."
        />
        <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
          13F is a lagged holdings snapshot, not a buy or sell tape. Form 144 notices are proposed sales, not confirmed
          sales.
        </p>
      </WorkspacePanel>
    </div>
  );
}
